import React from "react";
import {
    Chart,
    ChartArea,
    ChartLegend,
    ChartSeries,
    ChartSeriesItem,
    ChartXAxis,
    ChartYAxis,
    ChartXAxisItem,
    ChartYAxisItem,
    ChartTooltip,
} from "@progress/kendo-react-charts";

interface PumpCapacityCurveGraphData {
    maxScales: {
        capacity: number;
        head: number;
        bhp: number;
        efficiency: number;
    };
    bepbhp: any[];
    minBHP: any[];
    maxBHP: any[];
    head: any[];
    bhp: any[];
    efficiency: any[];
    target: any[];
    targetH: any[];
    targetV: any[];
    units: {
        capacity: string;
        head: string;
        bhp: string;
    };
}

interface PumpCapacityCurveGraphProps {
    data: PumpCapacityCurveGraphData;
}

const PumpCapacityCurveGraph: React.FC<PumpCapacityCurveGraphProps> = ({
    data,
}) => {
    if (!data || data === undefined) return <div>No data available</div>;

    const headScaleUnit = data.maxScales.head <= 1000 ? 100 : 1000;
    const powerScaleUnit =
        data.maxScales.bhp <= 1000 && data.maxScales.bhp > 500 ? 100 : 50;
    const xAxisMax = Math.ceil(data.maxScales.capacity / 1000) * 1000;
    const yAxisHeadMax =
        Math.ceil(data.maxScales.head / headScaleUnit) * headScaleUnit;
    const yAxisPowerMax =
        Math.ceil(data.maxScales.bhp / powerScaleUnit) * powerScaleUnit;
    const yAxisEfficiencyMax = Math.ceil(data.maxScales.efficiency / 10) * 10;
    const xAxisCrossingValues = [0, xAxisMax, xAxisMax];
    const bepFilterData = data.bepbhp.filter((_, index) => index % 2 === 0);
    const minFilterData = data.minBHP.filter((_, index) => index % 2 === 0);
    const maxFilterData = data.maxBHP.filter((_, index) => index % 2 === 0);

    return (
        <Chart>
            <ChartArea background="transparent" />

            <ChartLegend position="bottom" labels={{ color: "#fff" }} />

            <ChartSeries>
                <ChartSeriesItem
                    name="Head"
                    type="scatterLine"
                    data={data.head}
                    xField="capacity"
                    yField="head"
                    color="#004EB3"
                    yAxis="Head"
                    style="smooth"
                    markers={{ visible: false }}
                    width={4.0}
                    tooltip={{ visible: true, format: "{0} x {1}" }}
                />
                <ChartSeriesItem
                    name="Power"
                    type="scatterLine"
                    data={data.bhp}
                    xField="capacity"
                    yField="bhp"
                    color="#FF0000"
                    yAxis="Power"
                    markers={{ visible: false }}
                    width={4.0}
                    style="smooth"
                    tooltip={{ visible: true, format: "{0} x {1}" }}
                />
                <ChartSeriesItem
                    name="Efficiency"
                    type="scatterLine"
                    data={data.efficiency}
                    xField="capacity"
                    yField="efficiency"
                    color="#006633"
                    yAxis="Efficiency"
                    markers={{ visible: false }}
                    width={4.0}
                    style="smooth"
                    tooltip={{ visible: true, format: "{0} x {1}" }}
                />
                <ChartSeriesItem
                    name="MinFlow"
                    type="scatterLine"
                    data={data.minBHP}
                    xField="capacity"
                    yField="head"
                    color="#737373"
                    yAxis="Head"
                    markers={{
                        visible: true,
                        size: 5
                    }}
                    width={2.0}
                    dashType="longDash"
                    labels={{
                        visible: Boolean(minFilterData),
                        font: "10px calibri",
                        position: "top",
                        color: "darkgray",
                        background: "transparent",
                        padding: { bottom: 5 },
                        content: function (e) {
                            return e.dataItem.capacity;
                        },
                    }}
                    tooltip={{ visible: true, format: "{0}, {1}" }}
                    visibleInLegend={true}
                />
                <ChartSeriesItem
                    name="BEPFlow"
                    type="scatterLine"
                    data={data.bepbhp}
                    xField="capacity"
                    yField="head"
                    color="#737373"
                    yAxis="Head"
                    markers={{ visible: true, size: 10  }}
                    width={2.0}
                    dashType="longDash"
                    labels={{
                        visible: Boolean(bepFilterData),
                        font: "11px calibri",
                        position: "top",
                        color: "darkgray",
                        background: "transparent",
                        padding: { bottom: 5 },
                        content: function (e) {
                            return e.dataItem.capacity;
                        },
                    }}
                    tooltip={{ visible: true, format: "{0}, {1}" }}
                    visibleInLegend={true}
                />
                <ChartSeriesItem
                    name="MaxOperatingFlow"
                    type="scatterLine"
                    data={data.maxBHP}
                    xField="capacity"
                    yField="head"
                    color="#737373"
                    yAxis="Head"
                    markers={{ visible: true, size: 10  }}
                    width={2.0}
                    dashType="longDash"
                    labels={{
                        visible: Boolean(maxFilterData),
                        font: "11px calibri",
                        position: "top",
                        color: "darkgray",
                        background: "transparent",
                        padding: { bottom: 5 },
                        content: function (e) {
                            return e.dataItem.capacity;
                        },
                    }}
                    tooltip={{ visible: true, format: "{0}, {1}" }}
                    visibleInLegend={true}
                />
                <ChartSeriesItem
                    name="Target"
                    type="scatter"
                    data={data.target}
                    xField="capacity"
                    yField="head"
                    color="#4d4d4d"
                    yAxis="Head"
                    markers={{
                        visible: true,
                        type: "square",
                        background: "black",
                        size: 15,
                    }}
                    width={2.0}
                    labels={{
                        visible: true,
                        background: "#bfbfbf",
                        border: { width: 1, color: "black" },
                        font: "11px calibri",
                    }}
                    tooltip={{ visible: true }}
                    visibleInLegend={false}
                />
                <ChartSeriesItem
                    name="TargetH"
                    type="scatterLine"
                    data={data.targetH}
                    xField="capacity"
                    yField="head"
                    color="#bfbfbf"
                    yAxis="Head"
                    markers={{ visible: true }}
                    width={2.0}
                    dashType="dot"
                    tooltip={{ visible: false }}
                    visibleInLegend={false}
                />
                <ChartSeriesItem
                    name="TargetV"
                    type="scatterLine"
                    data={data.targetV}
                    xField="capacity"
                    yField="head"
                    color="#bfbfbf"
                    yAxis="Head"
                    markers={{ visible: true, size: 10 }}
                    width={2.0}
                    dashType="dot"
                    tooltip={{ visible: false }}
                    visibleInLegend={false}
                />
            </ChartSeries>

            {/* X Axis */}
            <ChartXAxis>
                <ChartXAxisItem
                    name="Capacity"
                    title={{ text: `Capacity (${data.units.capacity})` }}
                    axisCrossingValue={xAxisCrossingValues}
                    majorGridLines={{ width: 0.8, color: "#A0A0A0" }}
                    minorGridLines={{ width: 0.3, color: "#A0A0A0", visible: false }}
                    color="#fff"
                    crosshair={{
                        visible: true,
                        width: 1,
                        color: "#005F7C",
                        tooltip: { visible: true, format: "#.##" },
                    }}
                    max={data.maxScales.capacity + 50}
                />
            </ChartXAxis>

            {/* Y Axis */}
            <ChartYAxis>
                <ChartYAxisItem
                    name="Head"
                    title={{ text: `Head (${data.units.head})` }}
                    min={0}
                    max={
                        yAxisHeadMax === data.maxScales.head
                            ? yAxisHeadMax + 500
                            : yAxisHeadMax
                    }
                    color="#004EB3"
                    majorGridLines={{ width: 0.8, color: "#253040",}}
                    minorGridLines={{ width: 0.3, color: "#253040", visible: false }}
                    crosshair={{ visible: true, width: 1, color: "#005F7C" }}
                />
                <ChartYAxisItem
                    name="Power"
                    title={{ text: `Power (${data.units.bhp})` }}
                    min={0}
                    max={
                        yAxisPowerMax === data.maxScales.bhp
                            ? yAxisPowerMax + 50
                            : yAxisPowerMax
                    }
                    color="#FF0000"
                    crosshair={{ visible: true, width: 1, color: "#005F7C" }}
                />
                <ChartYAxisItem
                    name="Efficiency"
                    title={{ text: `Efficiency (%)` }}
                    min={0}
                    max={
                        yAxisEfficiencyMax === data.maxScales.efficiency
                            ? yAxisEfficiencyMax + 10
                            : yAxisEfficiencyMax
                    }
                    color="#006633"
                    crosshair={{ visible: true, width: 1, color: "#005F7C" }}
                />
            </ChartYAxis>

            {/* Tooltip */}
            <ChartTooltip visible={true} />
        </Chart>
    );
};

export default React.memo(PumpCapacityCurveGraph);

